import React, { useState, useEffect } from 'react';
import { toast } from './Toast';
import { X, FileText, Download, Folder } from 'lucide-react';
import { crmApi } from '../services/crmApi';

interface Props {
  applicationId: number;
  onClose: () => void;
}

interface DocumentTemplate {
  name: string;
  category: 'listy_intencyjne' | 'oswiadczenia' | 'referencje' | 'umowy' | 'zaswiadczenia';
  description: string;
}

const DOCUMENT_TEMPLATES: DocumentTemplate[] = [
  {
    name: 'LIST INTENCYJNY_FIRMA.docx',
    category: 'listy_intencyjne',
    description: 'List intencyjny od firmy/przedsiębiorstwa - współpraca biznesowa'
  },
  {
    name: 'LIST INTENCYJNY_FIRMA_2.docx',
    category: 'listy_intencyjne',
    description: 'List intencyjny od firmy - wariant 2 (dostawca/producent)'
  },
  {
    name: 'LIST INTENCYJNY_FIRMA_3.docx',
    category: 'listy_intencyjne',
    description: 'List intencyjny od firmy - wariant 3 (partner biznesowy)'
  },
  {
    name: 'LIST INTENCYJNY_HURTOWNIA.docx',
    category: 'listy_intencyjne',
    description: 'List intencyjny od hurtowni - zamiar zakupu towarów'
  },
  {
    name: 'LIST INTENCYJNY_HURTOWNIA_2.docx',
    category: 'listy_intencyjne',
    description: 'List intencyjny od hurtowni - wariant 2'
  },
  {
    name: 'LIST INTENCYJNY_OSOBA PRYWATNA.docx',
    category: 'listy_intencyjne',
    description: 'List intencyjny od osoby prywatnej - potencjalny klient'
  },
  {
    name: 'LIST INTENCYJNY_OSOBA PRYWATNA_2.docx',
    category: 'listy_intencyjne',
    description: 'List intencyjny od osoby prywatnej - wariant 2'
  },
  {
    name: 'LIST INTENCYJNY_OSOBA PRYWATNA_3.docx',
    category: 'listy_intencyjne',
    description: 'List intencyjny od osoby prywatnej - wariant 3'
  },
  {
    name: 'OŚWIADCZENIE_MAŁOŻONKA.docx',
    category: 'oswiadczenia',
    description: 'Oświadczenie małżonka o zgodzie na prowadzenie działalności i zaciąganie zobowiązań'
  },
  {
    name: 'REFERENCJE SALON.docx',
    category: 'referencje',
    description: 'Referencje dla salonu usługowego (fryzjer, kosmetyka, itp.)'
  },
  {
    name: 'REFERENCJE_OSOBA FIZYCZNA_KLIENT_ZNAJOMI.docx',
    category: 'referencje',
    description: 'Referencje od osoby fizycznej - klient/znajomy'
  },
  {
    name: 'REFERENCJE_OSOBA FIZYCZNA_KLIENT_ZNAJOMI_2.docx',
    category: 'referencje',
    description: 'Referencje od osoby fizycznej - wariant 2'
  },
  {
    name: 'UMOWA NAJMU LOKALU.doc',
    category: 'umowy',
    description: 'Wzór umowy najmu lokalu użytkowego pod działalność gospodarczą'
  },
  {
    name: 'UMOWA_UZYCZENIA_LOKALU_WZÓR (1).doc',
    category: 'umowy',
    description: 'Wzór umowy użyczenia lokalu (nieodpłatne użytkowanie)'
  },
  {
    name: 'ZAŚWIADCZENIE O PRAKTYKACH.docx',
    category: 'zaswiadczenia',
    description: 'Zaświadczenie o odbyciu praktyk/stażu zawodowego'
  }
];

const CATEGORY_LABELS = {
  listy_intencyjne: 'Listy intencyjne',
  oswiadczenia: 'Oświadczenia',
  referencje: 'Referencje',
  umowy: 'Umowy',
  zaswiadczenia: 'Zaświadczenia'
};

const CATEGORY_COLORS = {
  listy_intencyjne: 'bg-blue-50 border-blue-200 text-blue-700',
  oswiadczenia: 'bg-green-50 border-green-200 text-green-700',
  referencje: 'bg-purple-50 border-purple-200 text-purple-700',
  umowy: 'bg-orange-50 border-orange-200 text-orange-700',
  zaswiadczenia: 'bg-pink-50 border-pink-200 text-pink-700'
};

export const DocumentTemplatesSelector: React.FC<Props> = ({ applicationId, onClose }) => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  const filteredTemplates = selectedCategory
    ? DOCUMENT_TEMPLATES.filter(t => t.category === selectedCategory)
    : DOCUMENT_TEMPLATES;

  const handleDownloadTemplate = async (templateName: string) => {
    setDownloading(templateName);
    try {
      const blob = await crmApi.downloadDocumentTemplate(templateName);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = templateName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Błąd podczas pobierania szablonu');
    } finally {
      setDownloading(null);
    }
  };

  const getCategoryCount = (category: string) => {
    return DOCUMENT_TEMPLATES.filter(t => t.category === category).length;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Folder className="mr-3" size={28} />
              <div>
                <h2 className="text-2xl font-bold">Szablony Dokumentów</h2>
                <p className="text-blue-100 text-sm">15 gotowych wzorów dokumentów do pobrania</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Category Filters */}
        <div className="p-6 border-b bg-gray-50">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                selectedCategory === null
                  ? 'bg-blue-600 text-white'
                  : 'bg-white border-2 border-gray-300 text-gray-700 hover:border-blue-400'
              }`}
            >
              Wszystkie ({DOCUMENT_TEMPLATES.length})
            </button>
            {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setSelectedCategory(key)}
                className={`px-4 py-2 rounded-lg font-medium transition border-2 ${
                  selectedCategory === key
                    ? CATEGORY_COLORS[key as keyof typeof CATEGORY_COLORS]
                    : 'bg-white border-gray-300 text-gray-700 hover:border-blue-400'
                }`}
              >
                {label} ({getCategoryCount(key)})
              </button>
            ))}
          </div>
        </div>

        {/* Templates List */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredTemplates.map((template, index) => (
              <div
                key={index}
                className={`border-2 rounded-lg p-4 hover:shadow-lg transition ${
                  CATEGORY_COLORS[template.category]
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-start flex-1">
                    <FileText className="mr-3 flex-shrink-0 mt-1" size={20} />
                    <div className="flex-1">
                      <h3 className="font-bold text-sm mb-1">{template.name}</h3>
                      <p className="text-xs text-gray-600 mb-2">{template.description}</p>
                      <span className="inline-block text-xs px-2 py-1 rounded-full bg-white bg-opacity-50">
                        {CATEGORY_LABELS[template.category]}
                      </span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => handleDownloadTemplate(template.name)}
                  disabled={downloading === template.name}
                  className="w-full mt-3 bg-white border-2 border-current text-current py-2 rounded-lg font-medium hover:bg-opacity-80 transition disabled:opacity-50 flex items-center justify-center text-sm"
                >
                  <Download className="mr-2" size={16} />
                  {downloading === template.name ? 'Pobieranie...' : 'Pobierz szablon'}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <div className="flex items-center">
              <FileText className="mr-2" size={18} />
              <span>Formaty: DOCX, DOC | Szablony można edytować w Microsoft Word</span>
            </div>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition"
            >
              Zamknij
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
